"use client";

import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/app/_module/utils/utils";
import { formatScore } from "./ForumService";
import { useVoteMutation } from "../hooks/useVoteMutations";
import { useForumAccess } from "../hooks/useForumAccess";

interface VoteControlProps {
  targetType: "post" | "comment";
  targetId: string;
  score: number;
  myVote: 1 | -1 | null;
  orientation?: "vertical" | "horizontal";
  size?: "sm" | "md";
  disabled?: boolean;
}

// Two arrows and a number. The number is the thing — set in the mono face,
// tabular, and tinted only when it's your vote.
export function VoteControl({
  targetType,
  targetId,
  score,
  myVote,
  orientation = "vertical",
  size = "md",
  disabled,
}: VoteControlProps) {
  const voteMutation = useVoteMutation();
  const { requireWrite } = useForumAccess();

  const cast = (dir: 1 | -1) => {
    if (disabled) return;
    requireWrite(() => {
      const next = myVote === dir ? 0 : dir;
      voteMutation.mutate({ targetType, targetId, value: next as 1 | -1 | 0 });
    });
  };

  const iconSize = size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]";
  // Up is the brand blue; down is red. Your own vote tints the number too.
  const btn = (active: boolean, down = false) =>
    cn(
      "flex items-center justify-center rounded-md transition-colors disabled:opacity-40 disabled:pointer-events-none focus:outline-none focus-visible:ring-2",
      size === "sm" ? "h-6 w-7" : "h-7 w-8",
      down ? "focus-visible:ring-red-400/50" : "focus-visible:ring-primary/40",
      active
        ? down
          ? "bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400"
          : "bg-primary/10 text-primary"
        : down
          ? "text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          : "text-muted-foreground hover:bg-muted hover:text-foreground",
    );

  return (
    <div
      className={cn(
        "flex select-none items-center",
        orientation === "vertical" ? "flex-col gap-0" : "flex-row gap-0.5 rounded-md border bg-card px-0.5",
      )}
    >
      <button
        type="button"
        aria-label="Upvote"
        aria-pressed={myVote === 1}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          cast(1);
        }}
        className={btn(myVote === 1)}
      >
        <ChevronUp className={iconSize} strokeWidth={2.5} />
      </button>
      <span
        className={cn(
          "font-mono font-semibold tabular-nums leading-none text-center",
          size === "sm" ? "text-[12px] min-w-[1.6rem]" : "text-[13px] min-w-[1.9rem]",
          myVote === 1 ? "text-primary" : myVote === -1 ? "text-red-600 dark:text-red-400" : "text-foreground",
        )}
      >
        {formatScore(score)}
      </span>
      <button
        type="button"
        aria-label="Downvote"
        aria-pressed={myVote === -1}
        disabled={disabled}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          cast(-1);
        }}
        className={btn(myVote === -1, true)}
      >
        <ChevronDown className={iconSize} strokeWidth={2.5} />
      </button>
    </div>
  );
}
